/**
 * Expo Ortho — product.
 * <product-info>            variant switching through section rendering, add to bag, mobile sticky bar
 * <product-gallery>         thumbnails, mobile slider with counter, zoom lightbox, 3D models
 * <product-recommendations> "Pairs well with" rail, loaded when it scrolls near the viewport
 * Quick view: [data-quick-view] links open #QuickAddModal with the "quick-add" section.
 * Depends on global.js (theme.utils, <theme-modal>) and cart.js (theme.cart).
 */
(function () {
  'use strict';

  const theme = window.theme;
  const utils = theme.utils;
  const mobile = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function setLoading(buttons, loading) {
    buttons.forEach((button) => {
      button.classList.toggle('is-loading', loading);
      if (loading) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  }

  /* ---------------------------------------------------------------------- */
  /* <product-info>                                                         */
  /* ---------------------------------------------------------------------- */

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this.updateUrl = this.dataset.updateUrl === 'true';
      this.controller = null;
      if (this.updateUrl) this.rememberViewed();

      this.addEventListener('change', (event) => {
        if (event.target.matches('input[data-option-value-id], select[data-option-select]')) {
          if (event.target.hasAttribute('data-sheet-option')) {
            const sheet = event.target.closest('theme-drawer');
            const groups = sheet ? Array.from(sheet.querySelectorAll('[data-sheet-group]')) : [];
            const complete = !sheet?.hasAttribute('data-combined') || groups.every((g) => g.querySelector('input:checked'));
            if (sheet && complete && typeof sheet.close === 'function') sheet.close();
          }
          this.onOptionChange(event.target);
        }
      });

      this.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-product-form]');
        if (!form) return;
        event.preventDefault();
        this.addToCart(form, event.submitter);
      });

      // Segmented options (colors) come pre-filled. When nothing else is left to choose, resolve the
      // variant straight away so the price, media and button match the pre-filled choice.
      const buy = this.querySelector('[data-region="buy"]');
      const first = this.querySelector('input[data-option-value-id]:checked');
      if (buy && buy.dataset.needsChoice === 'true' && first && !this.missingOptions().length) {
        this.onOptionChange(first);
      }

      this.setupStickyBar();
    }

    get gallery() {
      return this.querySelector('product-gallery');
    }

    /** Selected option value ids, in option order (radios and selects). */
    selectedOptionIds() {
      const ids = [];
      this.querySelectorAll('input[data-option-value-id]:checked, select[data-option-select]').forEach((el) => {
        ids.push(el.matches('select') ? el.value : el.dataset.optionValueId);
      });
      return ids;
    }

    /** Re-renders this section for the chosen options and swaps every [data-region]. */
    // Keeps the last 12 viewed product handles for the "Recently viewed" section.
    rememberViewed() {
      const handle = (this.dataset.productUrl || '').split('?')[0].split('/').filter(Boolean).pop();
      if (!handle) return;
      try {
        const key = 'expo:recently-viewed';
        const list = JSON.parse(window.localStorage.getItem(key) || '[]').filter((h) => h !== handle);
        list.unshift(handle);
        window.localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
      } catch (error) {
        /* private mode / storage blocked: nothing to remember */
      }
    }

    /** Options (fieldsets / selects) the customer has not chosen yet. */
    missingOptions() {
      const groups = Array.from(this.querySelectorAll('.product-option:not(.product-option--combined), .product-tiers'));
      return groups.filter((group) => {
        const select = group.querySelector('select[data-option-select]');
        if (select) return !select.value;
        return group.querySelector('input[data-option-value-id]') && !group.querySelector('input[data-option-value-id]:checked');
      });
    }

    /** Scrolls to the first unchosen option, opens its sheet and explains what is missing. */
    clearChoiceErrors() {
      this.querySelectorAll('[data-choice-error]').forEach((el) => { el.hidden = true; el.textContent = ''; });
    }

    promptForChoice() {
      const missing = this.missingOptions()[0];
      if (!missing) return false;
      const labelEl = missing.querySelector('legend, .field__label, .drawer__title');
      const label = (missing.dataset.optionName || (labelEl ? labelEl.textContent : '')).replace(/:.*$/, '').trim().toLowerCase();
      const message = theme.strings.chooseOption.replace('{{ option }}', label || 'option');
      // Show the prompt right under the option that is missing; fall back to the button's message slot.
      this.querySelectorAll('[data-choice-error]').forEach((el) => { el.hidden = true; el.textContent = ''; });
      const inline = missing.querySelector('[data-choice-error]');
      const error = inline || this.querySelector('[data-form-error]');
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
      utils.announce(message);
      const shared = missing.closest('.product-option--combined');
      const trigger = missing.querySelector('.product-sheet-trigger') || (shared && shared.querySelector('.product-sheet-trigger'));
      // Pulse the trigger button, never the box that contains a fixed sheet: a transform on that
      // box would make the sheet position itself relative to it and jump when the pulse ends.
      const attention = trigger || missing;
      attention.classList.add('is-attention');
      setTimeout(() => attention.classList.remove('is-attention'), 1300);
      if (trigger) {
        // Bottom sheet: open it straight away (no page scroll underneath, so it slides up cleanly).
        trigger.click();
      } else {
        missing.scrollIntoView({ block: 'center', behavior: 'smooth' });
        (missing.querySelector('input[data-option-value-id], select') || missing).focus({ preventScroll: true });
      }
      return true;
    }

    /** Shows the chosen value on a "Select a size" trigger before the server re-render. */
    reflectChoice(input) {
      const combined = input.closest('.product-option--combined');
      if (combined) {
        const label = combined.querySelector('.product-sheet-trigger__label');
        const values = Array.from(combined.querySelectorAll('[data-sheet-group]'))
          .map((g) => g.querySelector('input:checked'))
          .filter(Boolean)
          .map((i) => i.value);
        if (label && values.length) label.textContent = values.join(' · ');
        return;
      }
      const option = input.closest('.product-option, .product-tiers');
      const selected = option && option.querySelector('[data-option-selected]');
      if (selected) selected.textContent = input.value;
      // Segmented control: slide the pill to the chosen value.
      const segmented = input.closest('[data-segmented]');
      if (segmented) {
        const inputs = Array.from(segmented.querySelectorAll('input[data-option-value-id]'));
        segmented.style.setProperty('--active', String(Math.max(0, inputs.indexOf(input))));
      }
      const trigger = option && option.querySelector('.product-sheet-trigger__label');
      if (!trigger) return;
      const titleEl = option.querySelector('.drawer__title');
      const title = titleEl ? titleEl.textContent.trim() : '';
      trigger.innerHTML = `<span class="product-sheet-trigger__name">${title}:</span> ${input.value}`;
    }

    async onOptionChange(input) {
      this.clearChoiceErrors();
      this.reflectChoice(input);
      const error = this.querySelector('[data-form-error]');
      if (error) error.hidden = true;
      // Wait until every option has a value before asking the server for the variant.
      if (this.missingOptions().length) return;

      const url = new URL(this.productUrl, window.location.origin);
      url.searchParams.set('option_values', this.selectedOptionIds().join(','));
      url.searchParams.set('section_id', this.sectionId);

      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error(response.statusText);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        this.render(doc, input.id);
      } catch (error) {
        // An aborted request was superseded by a newer choice: nothing to do.
        if (error.name !== 'AbortError') utils.announce(theme.strings.cartError);
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.removeAttribute('aria-busy');
        }
      }
    }

    render(doc, focusId) {
      const fresh =
        doc.querySelector(`product-info[data-section-id="${CSS.escape(this.sectionId)}"]`) ||
        doc.querySelector('product-info');
      if (!fresh) return;

      const quantityInput = this.querySelector('input[name="quantity"]');
      const quantity = quantityInput ? quantityInput.value : null;

      this.querySelectorAll('[data-region]').forEach((region) => {
        const next = fresh.querySelector(`[data-region="${CSS.escape(region.dataset.region)}"]`);
        if (next) region.replaceWith(next);
      });

      // Keep the quantity the customer typed (within the new variant's rule).
      const nextQuantity = this.querySelector('input[name="quantity"]');
      if (nextQuantity && quantity !== null) {
        nextQuantity.value = quantity;
        const stepper = nextQuantity.closest('quantity-input');
        if (stepper && typeof stepper.clamp === 'function') stepper.clamp();
      }

      if (focusId) {
        const focused = this.querySelector(`#${CSS.escape(focusId)}`);
        if (focused) focused.focus({ preventScroll: true });
      }

      const buy = this.querySelector('[data-region="buy"]');
      const data = buy ? buy.dataset : {};
      const variant = data.variantId
        ? {
            id: Number(data.variantId),
            available: data.variantAvailable === 'true',
            price: Number(data.variantPrice),
            compare_at_price: data.variantCompare ? Number(data.variantCompare) : null,
          }
        : null;

      if (this.updateUrl) {
        const pageUrl = new URL(window.location.href);
        if (variant) pageUrl.searchParams.set('variant', variant.id);
        else pageUrl.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', pageUrl);
      }

      if (data.mediaId && this.gallery) this.gallery.setActiveMedia(data.mediaId);

      utils.emit('variant:changed', { sectionId: this.sectionId, variant, productUrl: this.productUrl });
    }

    async addToCart(form, submitter) {
      const button = form.querySelector('[type="submit"]');
      if (!button || button.disabled || button.classList.contains('is-loading')) return;
      const buy = form.closest('[data-region="buy"]');
      if ((buy && buy.dataset.needsChoice === 'true') || this.missingOptions().length) {
        this.promptForChoice();
        return;
      }

      if (!theme.cart) {
        form.submit();
        return;
      }

      const buttons = submitter && submitter !== button ? [button, submitter] : [button];
      const error = form.querySelector('[data-form-error]');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
      setLoading(buttons, true);

      try {
        await theme.cart.add(new FormData(form), { trigger: submitter || button });
        utils.announce(theme.strings.added);

        // Quick add: the bag takes over (drawer or cart page).
        const modal = this.closest('theme-modal');
        if (modal && typeof modal.close === 'function') modal.close();
      } catch (err) {
        const message = err.message || theme.strings.cartError;
        if (error) {
          error.textContent = message;
          error.hidden = false;
        }
        utils.announce(message);
      } finally {
        setLoading(buttons, false);
      }
    }

    /** Phones: the bar appears once the buy buttons scroll out above, and hides at the footer. */
    setupStickyBar() {
      const bar = this.querySelector('[data-sticky-atc]');
      const buyBlock = this.querySelector('[data-buy-block]');
      if (!bar || !buyBlock || !('IntersectionObserver' in window)) return;

      let buyVisible = true;
      let buyAbove = false;
      let footerVisible = false;

      const update = () => {
        const show = mobile.matches && !buyVisible && buyAbove && !footerVisible;
        bar.classList.toggle('is-visible', show);
        bar.inert = !show;
        document.body.classList.toggle('sticky-atc-visible', show);
      };

      new IntersectionObserver(([entry]) => {
        buyVisible = entry.isIntersecting;
        buyAbove = entry.boundingClientRect.bottom < 0;
        update();
      }).observe(buyBlock);

      // Hide only once the page is scrolled to its very end (the footer's bottom bar), so short
      // product pages still get the bar.
      const footerEnd = document.querySelector('.footer__bottom, footer .footer__bottom');
      if (footerEnd) {
        new IntersectionObserver(([entry]) => {
          footerVisible = entry.isIntersecting;
          update();
        }).observe(footerEnd);
      }

      mobile.addEventListener('change', update);
    }
  }

  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

  /* ---------------------------------------------------------------------- */
  /* <product-gallery>                                                      */
  /* ---------------------------------------------------------------------- */

  const THUMB_KEYS = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.viewer = this.querySelector('[data-gallery-viewer]');
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-media-target]'));
      this.zoomModal = this.querySelector('[data-gallery-zoom-modal]');

      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-media-target]');
        if (thumb) {
          this.setActiveMedia(thumb.dataset.mediaTarget);
          return;
        }
        const zoom = event.target.closest('[data-gallery-zoom]');
        if (zoom) this.openZoom(zoom);
      });

      // Arrow keys move between thumbnails and show that media.
      this.addEventListener('keydown', (event) => {
        const thumb = event.target.closest('[data-media-target]');
        const delta = THUMB_KEYS[event.key];
        if (!thumb || !delta) return;
        event.preventDefault();
        const index = this.thumbs.indexOf(thumb);
        const next = this.thumbs[(index + delta + this.thumbs.length) % this.thumbs.length];
        next.focus();
        this.setActiveMedia(next.dataset.mediaTarget);
      });

      // Mobile slider: the item mostly in view becomes active (counter, dots).
      if (this.items.length > 1 && this.viewer && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!mobile.matches) return;
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.markActive(entry.target.dataset.mediaId);
            });
          },
          { root: this.viewer, threshold: 0.6 }
        );
        this.items.forEach((item) => this.observer.observe(item));
      }

      // The selected variant's media may not be first: start the slider on it.
      const active = this.items.find((item) => item.classList.contains('is-active'));
      if (active && mobile.matches) this.scrollToItem(active, false);

      this.loadModels();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    /** Updates the active classes, thumbnail states, dots and counter. */
    markActive(id) {
      const mediaId = String(id);
      let index = -1;

      this.items.forEach((item, i) => {
        const active = item.dataset.mediaId === mediaId;
        item.classList.toggle('is-active', active);
        if (active) index = i;
      });
      if (index === -1) return;

      this.thumbs.forEach((thumb) => {
        thumb.setAttribute('aria-pressed', String(thumb.dataset.mediaTarget === mediaId));
      });
      this.querySelectorAll('[data-gallery-dot]').forEach((dot) => {
        dot.classList.toggle('is-active', dot.dataset.galleryDot === mediaId);
      });
      const counter = this.querySelector('[data-gallery-index]');
      if (counter) counter.textContent = index + 1;
    }

    /** Shows the media with this id (thumbnail click, variant change). */
    setActiveMedia(id) {
      const item = this.items.find((candidate) => candidate.dataset.mediaId === String(id));
      if (!item) return;

      this.markActive(id);
      this.items.forEach((other) => {
        if (other !== item) other.querySelectorAll('video').forEach((video) => video.pause());
      });

      if (mobile.matches) {
        this.scrollToItem(item, !reducedMotion.matches);
      } else if (this.dataset.layout === 'grid') {
        const rect = item.getBoundingClientRect();
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          item.scrollIntoView({ block: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        }
      }
    }

    scrollToItem(item, smooth) {
      if (!this.viewer) return;
      const padding = parseFloat(getComputedStyle(this.viewer).paddingLeft) || 0;
      const left =
        item.getBoundingClientRect().left - this.viewer.getBoundingClientRect().left + this.viewer.scrollLeft - padding;
      this.viewer.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
    }

    openZoom(button) {
      if (!this.zoomModal || typeof this.zoomModal.open !== 'function') return;
      const frame = this.zoomModal.querySelector('[data-zoom-frame]');
      if (!frame) return;

      const image = document.createElement('img');
      image.className = 'product-gallery__zoom-image';
      image.src = button.dataset.galleryZoom;
      image.alt = button.dataset.zoomAlt || '';
      if (button.dataset.zoomWidth) image.width = Number(button.dataset.zoomWidth);
      if (button.dataset.zoomHeight) image.height = Number(button.dataset.zoomHeight);

      frame.replaceChildren(image);
      this.zoomModal.open(button);
    }

    /** 3D models need Shopify's model-viewer UI library. */
    loadModels() {
      const models = this.querySelectorAll('model-viewer');
      if (!models.length || !window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

      window.Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: () => {
            if (!window.Shopify.ModelViewerUI) return;
            models.forEach((model) => new window.Shopify.ModelViewerUI(model));
          },
        },
      ]);
    }
  }

  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ---------------------------------------------------------------------- */
  /* <product-recommendations>                                              */
  /* ---------------------------------------------------------------------- */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.ready || !this.dataset.url) return;
      this.ready = true;

      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: '400px 0px' }
      );
      observer.observe(this);
    }

    async load() {
      const designMode = Boolean(window.Shopify && window.Shopify.designMode);
      try {
        const doc = await utils.fetchHTML(this.dataset.url);
        const fresh = doc.querySelector('product-recommendations');
        const hasProducts = fresh && fresh.querySelector('.product-card[data-product-id]');

        if (hasProducts) {
          this.innerHTML = fresh.innerHTML;
          this.hidden = false;
        } else if (!designMode) {
          // Nothing to recommend: the section leaves the page (the editor keeps its placeholders).
          this.innerHTML = '';
          this.hidden = true;
        }
      } catch {
        if (!designMode) this.hidden = true;
      }
    }
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }

  /* ---------------------------------------------------------------------- */
  /* Quick view (multi-variant product cards)                               */
  /* ---------------------------------------------------------------------- */

  let quickViewSeq = 0;

  async function quickView(link, modal) {
    const seq = ++quickViewSeq;
    const content = modal.querySelector('[data-quick-add-content]');
    const loading = modal.querySelector('[data-quick-add-loading]');
    if (!content) return;

    content.replaceChildren(loading ? loading.content.cloneNode(true) : '');
    modal.open(link);

    const url = new URL(link.dataset.productUrl || link.getAttribute('href'), window.location.origin);
    url.searchParams.set('section_id', 'quick-add');

    try {
      const doc = await utils.fetchHTML(url.toString());
      if (seq !== quickViewSeq || !modal.isOpen) return;

      const info = doc.querySelector('product-info');
      if (!info) throw new Error('Quick add section missing');

      content.replaceChildren(document.adoptNode(info));
      const title = content.querySelector('.quick-add__title');
      if (title) utils.announce(title.textContent.trim());
    } catch {
      // The product page always works.
      if (seq === quickViewSeq) window.location.assign(url.pathname);
    }
  }

  // Restock alert sheet: channel toggle and the WhatsApp hand-off (email is a normal contact form).
  document.addEventListener('change', (event) => {
    const radio = event.target.closest('[data-restock-channel]');
    if (!radio) return;
    const root = radio.closest('[data-restock]');
    root.querySelectorAll('[data-restock-pane]').forEach((pane) => {
      pane.hidden = pane.dataset.restockPane !== radio.value;
    });
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-restock-whatsapp]');
    if (!button) return;
    const root = button.closest('[data-restock]');
    const phone = (root.querySelector('[data-restock-phone]') || {}).value || '';
    let text = button.dataset.restockText || '';
    if (phone.trim()) text += ` My WhatsApp: ${phone.trim()}`;
    window.open(`${button.dataset.restockWhatsapp}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    const drawer = button.closest('theme-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-quick-view]');
    if (!link) return;
    const modal = document.getElementById('QuickAddModal');
    if (!modal || typeof modal.open !== 'function') return;
    event.preventDefault();
    quickView(link, modal);
  });
})();
